import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { apiService } from "../services/api";
import { User } from "../types/generated";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Key, SaveIcon } from "lucide-react";

const Settings = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const { data: user, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const response = await apiService.getCurrentUser();
      const userData = response.data;
      setName(userData.name || "");
      setPhone(userData.phone || "");
      return userData;
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<User>) => apiService.updateCurrentUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      toast({
        title: t("settings.profile_updated"),
        description: t("settings.profile_updated_description"),
      });
    },
    onError: ({ message }) => {
      toast({
        title: t("common.error"),
        description: message || t("settings.profile_update_failed"),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate({ name, phone });
  };

  if (isLoading) return <div>{t("common.loading")}</div>;

  return (
    <div className="container mx-auto py-10">
      <div className="grid gap-6 max-w-2xl mx-auto">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t("settings.personal_details")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("settings.name")}</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("settings.enter_name")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("common.email")}</Label>
                <Input
                  id="email"
                  value={user?.email}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t("settings.phone")}</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, ""));
                  }}
                  placeholder={t("settings.enter_phone")}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={updateProfileMutation.isPending}
              >
                <SaveIcon className="h-4 w-4 mr-2" />
                {updateProfileMutation.isPending 
                  ? t("settings.updating") 
                  : t("settings.save_changes")}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.change_password")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                apiService
                  .updateCurrentUser({
                    old_password: oldPassword,
                    password: newPassword,
                  })
                  .then(() => {
                    toast({
                      title: t("settings.password_updated"),
                      description: t("settings.password_updated_description"),
                    });
                    apiService.logout();
                    queryClient.clear();
                    navigate("/login");
                  })
                  .catch(({ message }) => {
                    toast({
                      title: t("common.error"),
                      description: message || t("settings.password_update_failed"),
                      variant: "destructive",
                    });
                  });
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="old-password">{t("settings.current_password")}</Label>
                <Input
                  id="old-password"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder={t("settings.enter_current_password")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">{t("settings.new_password")}</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t("settings.enter_new_password")}
                />
              </div>

              <Button type="submit" className="w-full">
                <Key className="h-4 w-4 mr-2" />
                {t("settings.update_password")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
